/* import __COLOCATED_TEMPLATE__ from './fin-section.hbs'; */
/* RESPONSIBLE TEAM: team-knowledge-foundations */
import type AppService from 'embercom/services/app-service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { inject as service } from '@ember/service';
import type IntlService from 'ember-intl/services/intl';
import { trackedFunction } from 'ember-resources/util/function';
import {
  CAN_MANAGE_KNOWLEDGE_BASE_CONTENT,
  isAIAgentEntityType,
  isAICopilotEntityType,
} from 'embercom/lib/knowledge-hub/constants';
import { action } from '@ember/object';
import type KnowledgeHubEditorService from 'embercom/services/knowledge-hub-editor-service';
import { EntityType } from 'embercom/models/data/entity-types';
import type { LocalizedKnowledgeContent } from 'embercom/objects/knowledge-hub/localized-knowledge-content';
import type ExternalContent from 'embercom/models/external-content';
import type ArticleMultilingual from 'embercom/models/article-multilingual';
import { get, post } from 'embercom/lib/ajax';

export type AgentType = 'chatbot' | 'copilot' | 'sales_agent';

export interface ScheduleRecord {
  group_id: string;
  agents: AgentType[];
  transition_type: 'enable' | 'disable';
  start_at: string;
  end_at: string | null;
}

interface Args {
  activeContent: LocalizedKnowledgeContent;
  setCopilotAvailability?: (available: boolean) => any;
  setChatbotAvailability?: (available: boolean) => any;
}

const ARTICLE_LINK =
  'https://www.intercom.com/help/en/articles/9459957-enable-or-disable-content-for-fin-ai-agent-and-ai-copilot';

export default class FinSection extends Component<Args> {
  @service declare appService: AppService;
  @service declare notificationsService: $TSFixMe;
  @service declare permissionsService: $TSFixMe;
  @service declare knowledgeHubEditorService: KnowledgeHubEditorService;
  @service declare intl: IntlService;

  @tracked private article?: ArticleMultilingual | unknown;
  @tracked previewEnabled = false;
  @tracked isScheduleModalOpen = false;
  @tracked scheduledAgents: Array<{
    key: string;
    action: string;
    agents: string;
    dates: string;
    raw: ScheduleRecord;
  }> = [];
  @tracked availableAgents: AgentType[] = [];
  @tracked editingSchedule: ScheduleRecord | undefined = undefined;

  constructor(owner: any, args: Args) {
    super(owner, args);
    if ('article' in this.args.activeContent) {
      this.article = this.args.activeContent.article;
    }
    this.loadPreviewState();
    if (this.canShowScheduleAvailability) {
      this.loadScheduledAgents();
    }
  }

  get sectionDescription() {
    if (
      !this.isInternalArticle &&
      isAICopilotEntityType(this.args.activeContent.entityType) &&
      !isAIAgentEntityType(this.args.activeContent.entityType)
    ) {
      return this.intl.t(
        `${this.audienceSectionTranslationKeyPrefix}.fin-audience-description.copilot`,
      );
    }
    // Show copy that applies to both Fin AI Agent and Copilot
    return this.intl.t(`${this.audienceSectionTranslationKeyPrefix}.fin-audience-description.all`);
  }

  get audienceSectionTranslationKeyPrefix(): string {
    if (this.appService.app.canUseStandalone) {
      return 'knowledge-hub.content-editor.shared.audience-section.standalone';
    }
    return 'knowledge-hub.content-editor.shared.audience-section';
  }

  @action trackSegmentChangeAnalytics() {
    this.knowledgeHubEditorService.trackAnalyticsEvent('updated_fin_audience');
  }

  isEligibleForCopilot = trackedFunction(this, async () => {
    return this.args.activeContent.isEligibleForCopilot();
  });

  isEligibleForChatbot = trackedFunction(this, async () => {
    return this.args.activeContent.isEligibleForChatbot();
  });

  isEligibleForSalesAgent = trackedFunction(this, async () => {
    return this.args.activeContent.isEligibleForSalesAgent();
  });

  get isEnabledForChatbot() {
    return this.args.activeContent?.isEligibleAndAvailableToChatbot.value ?? false;
  }

  get isEnabledForCopilot() {
    return this.args.activeContent?.isEligibleAndAvailableToCopilot.value ?? false;
  }

  get isEnabledForSalesAgent() {
    return this.args.activeContent?.isEligibleAndAvailableToSalesAgent.value ?? false;
  }

  get isDisabledForChatbot() {
    return !this.isEligibleForChatbot.value;
  }

  get isDisabledForCopilot() {
    return !this.isEligibleForCopilot.value;
  }

  get isDisabledForSalesAgent() {
    return !this.isEligibleForSalesAgent.value;
  }

  get chatbotStampText() {
    return this.isEnabledForChatbot
      ? this.intl.t('knowledge-hub.content-editor.shared.visibility-section.enabled')
      : this.intl.t('knowledge-hub.content-editor.shared.visibility-section.disabled');
  }

  get copilotStampText() {
    return this.isEnabledForCopilot
      ? this.intl.t('knowledge-hub.content-editor.shared.visibility-section.enabled')
      : this.intl.t('knowledge-hub.content-editor.shared.visibility-section.disabled');
  }

  get salesAgentStampText() {
    return this.isEnabledForSalesAgent
      ? this.intl.t('knowledge-hub.content-editor.shared.visibility-section.enabled')
      : this.intl.t('knowledge-hub.content-editor.shared.visibility-section.disabled');
  }

  get isInternalArticle() {
    return this.args.activeContent.entityType === EntityType.InternalArticle;
  }

  get isArticleContent() {
    return this.args.activeContent.entityType === EntityType.ArticleContent;
  }

  get chatbotStampColor(): 'sky' | 'gray' {
    return this.isEnabledForChatbot ? 'sky' : 'gray';
  }

  get copilotStampColor(): 'sky' | 'gray' {
    return this.isEnabledForCopilot ? 'sky' : 'gray';
  }

  get previewStampText() {
    return this.previewEnabled
      ? this.intl.t('knowledge-hub.content-editor.shared.visibility-section.enabled')
      : this.intl.t('knowledge-hub.content-editor.shared.visibility-section.disabled');
  }

  get previewStampColor(): 'sky' | 'gray' {
    return this.previewEnabled ? 'sky' : 'gray';
  }

  get showTooltipForChatbot() {
    return this.isArticleContent || this.isInternalArticle;
  }

  get showTooltipForCopilot() {
    return this.isArticleContent;
  }

  get showTooltipForSalesAgent() {
    return this.isArticleContent || this.isInternalArticle;
  }

  get chatbotTooltipContent() {
    if (this.isInternalArticle && !this.appService.app.canEnableFinForInternalArticles) {
      return this.intl.t(
        `${this.visibilitySectionTranslationKeyPrefix}.chatbot-disabled-for-internal-article`,
      );
    } else {
      return this.intl.t('knowledge-hub.content-editor.shared.visibility-section.toggle-tooltip', {
        link: ARTICLE_LINK,
        htmlSafe: true,
      });
    }
  }

  get copilotTooltipContent() {
    return this.intl.t('knowledge-hub.content-editor.shared.visibility-section.toggle-tooltip', {
      link: ARTICLE_LINK,
      htmlSafe: true,
    });
  }

  get salesAgentTooltipContent() {
    return this.intl.t('knowledge-hub.content-editor.shared.visibility-section.toggle-tooltip', {
      link: ARTICLE_LINK,
      htmlSafe: true,
    });
  }

  get visibilitySectionTranslationKeyPrefix(): string {
    if (this.appService.app.canUseStandalone) {
      return 'knowledge-hub.content-editor.shared.visibility-section.standalone';
    }
    return 'knowledge-hub.content-editor.shared.visibility-section';
  }

  get serviceAgentLabel(): string {
    if (this.canShowSalesAgentToggle) {
      return this.appService.app.canUseStandalone
        ? this.intl.t(
            'knowledge-hub.content-editor.shared.visibility-section.standalone.fin-ai-agent-for-service',
          )
        : this.intl.t(
            'knowledge-hub.content-editor.shared.visibility-section.fin-ai-agent-for-service',
          );
    }
    return this.appService.app.canUseStandalone
      ? this.intl.t(
          'knowledge-hub.content-editor.shared.visibility-section.standalone.fin-ai-agent',
        )
      : this.intl.t('knowledge-hub.content-editor.shared.visibility-section.fin-ai-agent');
  }

  get salesAgentLabel(): string {
    return this.appService.app.canUseStandalone
      ? this.intl.t(
          'knowledge-hub.content-editor.shared.visibility-section.standalone.fin-ai-sales-agent',
        )
      : this.intl.t('knowledge-hub.content-editor.shared.visibility-section.fin-ai-sales-agent');
  }

  get canShowSalesAgentToggle(): boolean {
    return this.appService.app.canUseFeature('fin-sales-agent');
  }

  get canShowPreviewAvailabilityToggle(): boolean {
    return this.appService.app.canUseFeature('fin-draft-content-testing-enabled');
  }

  get canShowScheduleAvailability(): boolean {
    return (
      !this.isArticleContent &&
      this.appService.app.canUseFeature('team-knowledge-foundations-fin-availability-scheduling')
    );
  }

  async checkPermissionWithPopup() {
    await this.permissionsService.checkPermission(CAN_MANAGE_KNOWLEDGE_BASE_CONTENT);
  }

  @action
  async toggleChatbotVisibility() {
    try {
      await this.checkPermissionWithPopup();
    } catch (e) {
      return;
    }

    try {
      this.knowledgeHubEditorService.trackAnalyticsEvent('toggled_chatbot_visibility');
      this.knowledgeHubEditorService.trackAnalyticsEvent(
        this.isEnabledForChatbot ? 'disabled_chatbot_availability' : 'enabled_chatbot_availability',
      );
      await this.args.activeContent.toggleChatbotAvailability();
      if (this.isArticleContent) {
        await (this.article as ArticleMultilingual)?.reload();
      } else {
        (this.args.activeContent as ExternalContent).reload();
      }
    } catch (error) {
      this.notificationsService.notifyError(
        this.intl.t(`${this.visibilitySectionTranslationKeyPrefix}.chatbot-error`),
      );
    }
  }

  @action
  async toggleCopilotVisibility() {
    try {
      await this.checkPermissionWithPopup();
    } catch (e) {
      return;
    }

    try {
      this.knowledgeHubEditorService.trackAnalyticsEvent('toggled_copilot_visibility');
      this.knowledgeHubEditorService.trackAnalyticsEvent(
        this.isEnabledForCopilot ? 'disabled_copilot_availability' : 'enabled_copilot_availability',
      );
      await this.args.activeContent.toggleCopilotAvailability();
      if (this.isArticleContent) {
        await (this.article as ArticleMultilingual)?.reload();
      } else {
        (this.args.activeContent as ExternalContent).reload();
      }
    } catch (error) {
      this.notificationsService.notifyError(
        this.intl.t(`${this.visibilitySectionTranslationKeyPrefix}.copilot-error`),
      );
    }
  }

  @action
  async toggleSalesAgentVisibility() {
    try {
      await this.checkPermissionWithPopup();
    } catch (e) {
      return;
    }

    try {
      this.knowledgeHubEditorService.trackAnalyticsEvent('toggled_sales_agent_visibility');
      this.knowledgeHubEditorService.trackAnalyticsEvent(
        this.isEnabledForSalesAgent
          ? 'disabled_sales_agent_availability'
          : 'enabled_sales_agent_availability',
      );
      await this.args.activeContent.toggleSalesAgentAvailability();
      if (this.isArticleContent) {
        await (this.article as ArticleMultilingual)?.reload();
      } else {
        (this.args.activeContent as ExternalContent).reload();
      }
    } catch (error) {
      this.notificationsService.notifyError(
        this.intl.t(`${this.visibilitySectionTranslationKeyPrefix}.sales-agent-error`),
      );
    }
  }

  get copilotLabel() {
    return this.appService.app.canUseStandalone
      ? this.intl.t(
          'knowledge-hub.content-editor.shared.visibility-section.standalone.fin-ai-copilot',
        )
      : this.intl.t('knowledge-hub.content-editor.shared.visibility-section.fin-ai-copilot');
  }

  get scheduleAgentOptions(): Array<{ label: string; key: AgentType }> {
    let options: Array<{ label: string; key: AgentType }> = [
      { label: this.serviceAgentLabel, key: 'chatbot' },
      { label: this.copilotLabel, key: 'copilot' },
    ];

    if (this.canShowSalesAgentToggle) {
      options.push({ label: this.salesAgentLabel, key: 'sales_agent' });
    }

    return options;
  }

  get unscheduledAgentOptions() {
    return this.scheduleAgentOptions.filter((opt) => this.availableAgents.includes(opt.key));
  }

  get editAgentOptions() {
    if (!this.editingSchedule) {
      return this.unscheduledAgentOptions;
    }
    // In edit mode: show the agents already in this schedule + any available agents
    let keys = new Set([...this.editingSchedule.agents, ...this.availableAgents]);
    return this.scheduleAgentOptions.filter((opt) => keys.has(opt.key));
  }

  get canAddSchedule() {
    return this.unscheduledAgentOptions.length > 0;
  }

  @action
  openScheduleModal() {
    this.editingSchedule = undefined;
    this.isScheduleModalOpen = true;
  }

  @action
  openEditScheduleModal(raw: ScheduleRecord) {
    this.editingSchedule = raw;
    this.isScheduleModalOpen = true;
  }

  @action
  closeScheduleModal() {
    this.isScheduleModalOpen = false;
    this.editingSchedule = undefined;
  }

  private async loadScheduledAgents() {
    if (!(this.args.activeContent as any).entityId) {
      return;
    }
    try {
      let response = (await get('/ember/knowledge_hub/ai_availability_schedules', {
        app_id: this.appService.app.id,
        entity_type: this.args.activeContent.entityType,
        entity_id: (this.args.activeContent as any).entityId,
      })) as {
        ai_availability_schedules: Array<{
          group_id: string;
          agents: AgentType[];
          transition_type: 'enable' | 'disable';
          start_at: string;
          end_at: string | null;
        }>;
        available_agents: AgentType[];
      };

      this.availableAgents = response.available_agents;

      this.scheduledAgents = response.ai_availability_schedules.map((s) => {
        let agentLabels = s.agents.map((a) => {
          let option = this.scheduleAgentOptions.find((o) => o.key === a);
          return option ? option.label : a;
        });
        let dateStr = new Date(s.start_at).toLocaleDateString();
        let endStr = s.end_at ? ` – ${new Date(s.end_at).toLocaleDateString()}` : '';
        return {
          key: s.group_id,
          action:
            s.transition_type === 'enable'
              ? this.intl.t(
                  'knowledge-hub.content-editor.shared.schedule-availability-modal.action-enable',
                )
              : this.intl.t(
                  'knowledge-hub.content-editor.shared.schedule-availability-modal.action-disable',
                ),
          agents: agentLabels.join(' & '),
          dates: `${dateStr}${endStr}`,
          raw: s,
        };
      });
    } catch (_error) {
      // Non-critical — list stays empty
    }
  }

  @action
  async onScheduleCreated() {
    await this.loadScheduledAgents();
  }

  async loadPreviewState() {
    // Only load preview state for article content when feature flag is enabled
    if (
      !this.isArticleContent ||
      !this.canShowPreviewAvailabilityToggle ||
      !(this.args.activeContent as any).id
    ) {
      return;
    }

    try {
      let response = (await get(
        `/ember/article_contents/${(this.args.activeContent as any).id}/preview_availability_status`,
        {
          app_id: this.appService.app.id,
        },
      )) as { preview_enabled: boolean };

      this.previewEnabled = response.preview_enabled;
    } catch (error) {
      console.error('Failed to load preview availability status:', error);
      // Default to false if API call fails - don't show user notification for load errors
      this.previewEnabled = false;
    }
  }

  @action
  async togglePreviewAvailability() {
    if (
      !this.isArticleContent ||
      !this.canShowPreviewAvailabilityToggle ||
      !(this.args.activeContent as any).id
    ) {
      return;
    }

    try {
      await this.checkPermissionWithPopup();
    } catch (e) {
      return;
    }

    try {
      this.knowledgeHubEditorService.trackAnalyticsEvent('toggled_preview_availability');
      this.knowledgeHubEditorService.trackAnalyticsEvent(
        this.previewEnabled ? 'disabled_preview_availability' : 'enabled_preview_availability',
      );

      let response = (await post(
        `/ember/article_contents/${(this.args.activeContent as any).id}/toggle_preview_availability`,
        {
          app_id: this.appService.app.id,
        },
      )) as { id: number; preview_enabled: boolean };

      this.previewEnabled = response.preview_enabled;
      await (this.article as ArticleMultilingual)?.reload();
    } catch (error) {
      this.notificationsService.notifyError(
        this.intl.t(`${this.visibilitySectionTranslationKeyPrefix}.preview-availability-error`),
      );
    }
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'KnowledgeHub::ContentEditor::Shared::FinSection': typeof FinSection;
    'knowledge-hub/content-editor/shared/fin-section': typeof FinSection;
  }
}
